import { memo, useMemo, useState } from "react";
import UnifiedSidebar from "src/reusable/Sidebar/UnifiedSidebar";
import SidebarToggleButton from "src/reusable/Sidebar/SidebarToggleButton";
import { useResponsive } from "src/reusable/hooks/useResponsive";
import { useUIStore } from "src/views/assistant/stores/UIStore";
import { useChatStreamValue } from "src/reusable/Chat/ChatStreaming/hooks/useChatStream";
import { useVnvAssistSidebar } from "../hooks/useVnvAssistSidebar";
import { useVnvAssistStore } from "../store/VnvAssistStore";

const EMPTY_ARTIFACTS = {};

interface VnvAssistSidebarProps {
  responseSources: any[];
  dialogueId: number | undefined;
}

export const VnvAssistSidebar = memo(
  ({ responseSources, dialogueId }: VnvAssistSidebarProps) => {
    const { isMobile } = useResponsive();
    const { rightSidebar, setRightSidebar } = useUIStore();
    const claims = useVnvAssistStore((s) => s.claims);

    const [viewMode, setViewMode] = useState<"dataChunk" | "cardList">(
      "dataChunk",
    );

    // Read from the host's store (provided by VnvAssistChat).
    const artifacts = useChatStreamValue((s) => s.artifacts, EMPTY_ARTIFACTS);

    const sidebarContext = useMemo(
      () => ({
        dialogueId,
        responseSources,
        artifacts,
        viewMode,
        setViewMode,
      }),
      [dialogueId, responseSources, artifacts, viewMode],
    );

    const sidebarConfig = useVnvAssistSidebar(sidebarContext);

    const totalItems =
      claims.length + responseSources.length + Object.keys(artifacts).length;

    return (
      <>
        <UnifiedSidebar
          config={sidebarConfig}
          context={sidebarContext}
          open={rightSidebar.isOpen}
          onOpenChange={(open) => setRightSidebar({ isOpen: open })}
          selectedTab={rightSidebar.selectedItem}
          onTabChange={(tab) =>
            setRightSidebar({
              selectedItem: tab,
              isArtifactItemOpen: false,
              currentArtifactId: null,
            })
          }
          width={rightSidebar.width}
          onWidthChange={(width) => setRightSidebar({ width })}
        />

        {isMobile && (
          <SidebarToggleButton
            open={rightSidebar.isOpen}
            onClick={() =>
              setRightSidebar({
                isOpen: !rightSidebar.isOpen,
                ...(!rightSidebar.isOpen &&
                  rightSidebar.selectedItem === null && {
                    selectedItem: "claims",
                  }),
              })
            }
            badgeContent={totalItems}
            tooltipTitle={
              rightSidebar.isOpen ? "Close sidebar" : `View ${totalItems} items`
            }
            show={totalItems > 0}
          />
        )}
      </>
    );
  },
);

VnvAssistSidebar.displayName = "VnvAssistSidebar";

export default VnvAssistSidebar;
